import { sortProductCategories } from '@lib/data/statics/helpers'
import { useBreakpoint, useWindowSize } from '@lib/hooks'
import { ProductCategory, StoreGetProductsParams } from '@medusajs/medusa'
import Button from '@modules/bh/components/BHButton'
import { useCollections, useProductCategories } from 'medusa-react'
import { ChangeEvent, useMemo, useState } from 'react'
import { MdFilterListAlt } from 'react-icons/md'

type RefinementListProps = {
  refinementList: StoreGetProductsParams
  setRefinementList: (refinementList: StoreGetProductsParams) => void
}

const RefinementList = ({
  refinementList,
  setRefinementList,
}: RefinementListProps) => {
  const { collections, isLoading: isLoadingCollections } = useCollections()
  const {
    product_categories: rawProductCategories,
    isLoading: isLoadingProductCategories,
  } = useProductCategories()

  const orderedProductCategories = useMemo(() => {
    if (rawProductCategories === undefined) {
      return []
    }

    const copy = [...rawProductCategories]

    return sortProductCategories(copy)
  }, [rawProductCategories])

  const isLoading = isLoadingCollections || isLoadingProductCategories

  const handleCollectionChange = (
    e: ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const { checked } = e.target

    const collectionIds = refinementList.collection_id || []

    const exists = collectionIds.includes(id)

    if (checked && !exists) {
      setRefinementList({
        ...refinementList,
        collection_id: [...collectionIds, id],
      })

      return
    }

    if (!checked && exists) {
      setRefinementList({
        ...refinementList,
        collection_id: collectionIds.filter((c) => c !== id),
      })

      return
    }

    return
  }

  const getCategoryParentFromOrdered = (id: string): Maybe<ProductCategory> => {
    const category = orderedProductCategories.find((c) => c.id === id)

    if (category === undefined) {
      return null
    }

    const { parent_category_id } = category

    if (parent_category_id === null) {
      return null
    }

    return (
      orderedProductCategories.find((c) => c.id === parent_category_id) ?? null
    )
  }

  const handleCategoryChange = (
    e: ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const { checked } = e.target

    let productCategoryIds = [...(refinementList.category_id ?? [])]

    // If parent of clicked category is checked, uncheck it
    const parent = getCategoryParentFromOrdered(id)

    if (parent !== null) {
      const parentIsChecked = productCategoryIds.includes(parent.id)
      if (parentIsChecked) {
        productCategoryIds = productCategoryIds.filter((c) => c !== parent.id)
      }
    }

    const exists = productCategoryIds.includes(id)

    if (checked && !exists) {
      productCategoryIds.push(id)
    }

    if (!checked && exists) {
      productCategoryIds = productCategoryIds.filter((c) => c !== id)
    }

    setRefinementList({
      ...refinementList,
      category_id: productCategoryIds,
    })

    return
  }

  // For each category, get padding left value as a function of the number of ancestors the category has
  const getPaddingLeft = (category: ProductCategory): number => {
    const { parent_category_id } = category

    if (parent_category_id === null) {
      return 0
    }

    const parent = orderedProductCategories.find(
      (c) => c.id === parent_category_id
    )

    if (parent === undefined) {
      return 0
    }

    return getPaddingLeft(parent) + 24
  }

  const [showFilters, setShowFilters] = useState(false)
  const breakpoint = useBreakpoint()
  const filtersToggleable = ['xs', 'sm'].indexOf(breakpoint) > -1

  return (
    <div className="px-8 py-4 small:pr-0 small:pl-8 small:min-w-[250px]">
      {filtersToggleable && (
        <Button
          className="sm:hidden"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="flex items-center">
            <MdFilterListAlt />
            <span className="ml-2">Filters</span>
          </div>
        </Button>
      )}
      <div
        className={`transition-all overflow-hidden duration-300 ${
          !filtersToggleable
            ? 'max-h-[1000px]'
            : showFilters
            ? 'max-h-[1000px]'
            : 'max-h-[0px]'
        }`}
      >
        <div>
          <div className="flex gap-x-3 flex-col gap-y-3">
            <span className="text-base-semi">Collections</span>
            <ul className="text-base-regular flex items-center gap-x-4 small:grid small:grid-cols-1 small:gap-y-2">
              {collections?.map((c) => (
                <li key={c.id}>
                  <label className="flex items-center gap-x-2">
                    <input
                      type="checkbox"
                      defaultChecked={refinementList.collection_id?.includes(
                        c.id
                      )}
                      onChange={(e) => handleCollectionChange(e, c.id)}
                      className="accent-amber-200"
                    />
                    {c.title}
                  </label>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex mt-8 gap-x-3 flex-col gap-y-3">
            <span className="text-base-semi">Product Categories</span>
            <ul className="text-base-regular items-center gap-x-4 grid grid-cols-1 gap-y-2">
              {orderedProductCategories.map((c) => (
                <li key={c.id}>
                  <label
                    className={`flex items-center gap-x-2`}
                    style={{ paddingLeft: `${getPaddingLeft(c)}px` }}
                  >
                    <input
                      type="checkbox"
                      checked={refinementList.category_id?.includes(c.id)}
                      onChange={(e) => handleCategoryChange(e, c.id)}
                      className="accent-amber-200"
                    />
                    {c.name}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RefinementList
